import React, { useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { useChatData, useChatClient } from '../context/ChatContext';
import { isValidEthAddress, shortenAddress } from '../utils/chatHelpers';
import { resolveHnsName } from '../../../api/mcpApi';
import { useHnsNames } from '../hooks/useHnsNames';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface NewGroupModalProps {
  show: boolean;
  onHide: () => void;
}

interface MemberEntry {
  address: string;
  hnsUsername?: string; // e.g. "@alice"
}

const NewGroupModal: React.FC<NewGroupModalProps> = ({ show, onHide }) => {
  const { t } = useTranslation();
  const { createGroup, selectConversation } = useChatData();
  const { chatAddress } = useChatClient();
  const [groupName, setGroupName] = useState('');
  const [memberInput, setMemberInput] = useState('');
  const [members, setMembers] = useState<MemberEntry[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState('');

  // Resolve HNS names for members added by raw address
  const memberAddresses = members.map((m) => m.address);
  const { getDisplayName } = useHnsNames(memberAddresses);

  const handleAddMember = useCallback(async () => {
    const input = memberInput.trim();
    if (!input) return;

    setError('');

    let addr = input;
    let hnsUsername: string | undefined;

    // If not a raw 0x address, resolve via HNS
    if (!isValidEthAddress(input)) {
      setIsResolving(true);
      try {
        const result = await resolveHnsName(input);
        if (!result?.address) {
          setError(t('chat.usernameNotFound', 'Username not found'));
          setIsResolving(false);
          return;
        }
        addr = result.address;
        hnsUsername = input.startsWith('@') ? input : `@${input}`;
      } catch (err: any) {
        setError(err?.response?.data?.message || t('chat.usernameNotFound', 'Username not found'));
        setIsResolving(false);
        return;
      }
      setIsResolving(false);
    }

    if (!isValidEthAddress(addr)) {
      setError(t('chat.invalidAddress', 'Invalid Ethereum address'));
      return;
    }

    if (chatAddress && addr.toLowerCase() === chatAddress.toLowerCase()) {
      setError(t('chat.cannotAddSelf', 'You are already added as the group creator'));
      return;
    }

    if (members.some((m) => m.address === addr.toLowerCase())) {
      setError(t('chat.alreadyAdded', 'Address already added'));
      return;
    }

    setMembers((prev) => [...prev, { address: addr.toLowerCase(), hnsUsername }]);
    setMemberInput('');
    setError('');
  }, [memberInput, members, chatAddress, t]);

  const handleRemoveMember = (addr: string) => {
    setMembers((prev) => prev.filter((m) => m.address !== addr));
  };

  const handleCreate = useCallback(async () => {
    if (members.length < 1) {
      setError(t('chat.addMembers', 'Add at least one member'));
      return;
    }

    setIsCreating(true);
    try {
      const addresses = members.map((m) => m.address);
      const conversationId = await createGroup(addresses, groupName.trim() || undefined);
      if (conversationId) {
        selectConversation(conversationId);
        setGroupName('');
        setMembers([]);
        setMemberInput('');
        onHide();
      } else {
        setError(t('chat.createGroupFailed', 'Failed to create group'));
      }
    } catch (err: any) {
      setError(err.message || t('chat.createGroupFailed', 'Failed to create group'));
    } finally {
      setIsCreating(false);
    }
  }, [members, groupName, createGroup, selectConversation, onHide, t]);

  const handleClose = () => {
    setGroupName('');
    setMembers([]);
    setMemberInput('');
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="chat-modal">
      <Modal.Header closeButton>
        <Modal.Title>{t('chat.newGroup', 'New Group')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="chat-modal__field">
          <label className="chat-modal__label">
            {t('chat.groupName', 'Group Name')} ({t('chat.optional', 'optional')})
          </label>
          <input
            type="text"
            className="chat-search-input"
            placeholder={t('chat.groupNamePlaceholder', 'Enter group name')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </div>

        <div className="chat-modal__field">
          <label className="chat-modal__label">
            {t('chat.addMemberLabel', 'Add Member (Address or @username)')}
          </label>
          <div className="chat-modal__input-row">
            <input
              type="text"
              className="chat-search-input"
              placeholder="0x... or @username"
              value={memberInput}
              onChange={(e) => {
                setMemberInput(e.target.value);
                setError('');
              }}
              onKeyDown={(e) => e.key === 'Enter' && !isResolving && handleAddMember()}
            />
            <button
              className="chat-btn chat-btn--primary"
              onClick={handleAddMember}
              disabled={!memberInput.trim() || isResolving}
            >
              {isResolving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
            </button>
          </div>
          {error && <p className="chat-modal__error">{error}</p>}
        </div>

        {/* Member list */}
        {members.length > 0 && (
          <div className="chat-modal__members">
            <label className="chat-modal__label">
              {t('chat.members', 'Members')} ({members.length})
            </label>
            {members.map((member) => {
              const label = member.hnsUsername
                ? `${member.hnsUsername} (${shortenAddress(member.address, 6)})`
                : getDisplayName(member.address);
              return (
                <div key={member.address} className="chat-modal__member-item">
                  <span className="chat-modal__member-address" title={member.address}>{label}</span>
                  <button
                    className="chat-btn chat-btn--icon"
                    onClick={() => handleRemoveMember(member.address)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="chat-btn chat-btn--secondary" onClick={handleClose}>
          {t('chat.cancel', 'Cancel')}
        </button>
        <button
          className="chat-btn chat-btn--primary"
          onClick={handleCreate}
          disabled={isCreating || members.length < 1}
        >
          {isCreating ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin /> {t('chat.creating', 'Creating...')}
            </>
          ) : (
            t('chat.createGroup', 'Create Group')
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewGroupModal;
